import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Scales, CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalCard, BrutalButton } from "../components/Brutal";

// v409.3 — one-click stock vs movement-log audit (owner/admin/cfo).
export default function ReconciliationPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyFlags, setOnlyFlags] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reconciliation");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <p className="font-medium p-4">{t("common.loading")}</p>;
  if (!data) return null;

  const holders = onlyFlags ? data.holders.filter((h) => h.flags > 0) : data.holders;

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div>
        <Link to="/movement-log" className="inline-flex items-center gap-2 font-medium text-sm hover:underline mb-2">
          <ArrowLeft size={16} weight="bold" /> {t("recon.back")}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Scales size={26} weight="bold" /> {t("recon.title")}
        </h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">{t("recon.subtitle")}</p>
      </div>

      <div
        className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${
          data.total_flags > 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"
        }`}
        data-testid="recon-summary"
      >
        <div className="flex items-center gap-2 font-semibold">
          {data.total_flags > 0 ? (
            <><WarningCircle size={20} weight="fill" className="text-rose-600" />
              <span className="text-rose-800">{t("recon.flags_found", { n: data.total_flags })}</span></>
          ) : (
            <><CheckCircle size={20} weight="fill" className="text-emerald-600" />
              <span className="text-emerald-800">{t("recon.all_clear")}</span></>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer">
            <input
              type="checkbox"
              checked={onlyFlags}
              onChange={(e) => setOnlyFlags(e.target.checked)}
              className="h-4 w-4 accent-rose-600"
              data-testid="recon-only-flags-toggle"
            />
            {t("recon.only_flags")}
          </label>
          <BrutalButton size="sm" variant="secondary" onClick={load} disabled={loading} data-testid="recon-refresh">
            <ArrowsClockwise size={14} weight="bold" /> {t("recon.refresh")}
          </BrutalButton>
        </div>
      </div>

      {data.intake_suspects?.length > 0 && (
        <BrutalCard data-testid="recon-intake-suspects">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={18} weight="fill" className="text-amber-600" />
            <p className="font-semibold">{t("recon.suspects_title", { n: data.intake_suspects.length })}</p>
          </div>
          <p className="text-xs text-zinc-500 mb-3">{t("recon.suspects_hint")}</p>
          <div className="space-y-2">
            {data.intake_suspects.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="font-mono font-semibold">"{s.matched_from}"</span>
                <span className="text-zinc-400">→</span>
                <span className="font-medium">{s.product_name}</span>
                <span className="text-zinc-500 tabular-nums">+{s.quantity} {s.unit || "units"}</span>
                <span className="ml-auto text-[11px] text-zinc-400">{(s.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </BrutalCard>
      )}

      {holders.length === 0 ? (
        <BrutalCard>
          <p className="font-medium">{onlyFlags ? t("recon.no_flagged_holders") : t("recon.no_holders")}</p>
        </BrutalCard>
      ) : (
        holders.map((h) => <HolderCard key={h.id} holder={h} onlyFlags={onlyFlags} />)
      )}
    </div>
  );
}

function HolderCard({ holder, onlyFlags }) {
  const { t } = useTranslation();
  const rows = onlyFlags ? holder.rows.filter((r) => r.delta !== 0) : holder.rows;
  if (rows.length === 0) return null;
  return (
    <BrutalCard data-testid={`recon-holder-${holder.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="font-semibold">{holder.name}</p>
        {holder.code && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200 tabular-nums">{holder.code}</span>
        )}
        {holder.is_personal && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 uppercase">{t("recon.personal_tag")}</span>
        )}
        <span
          className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
            holder.flags > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
          }`}
        >
          {holder.flags > 0 ? t("recon.holder_flags", { n: holder.flags }) : t("recon.holder_ok")}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
              <th className="py-1.5 pr-3">{t("recon.col_product")}</th>
              <th className="py-1.5 px-2 text-right">{t("recon.col_taken")}</th>
              <th className="py-1.5 px-2 text-right">{t("recon.col_logged")}</th>
              <th className="py-1.5 px-2 text-right">{t("recon.col_returns")}</th>
              <th className="py-1.5 px-2 text-right">{t("recon.col_on_hand")}</th>
              <th className="py-1.5 pl-2 text-right">{t("recon.col_delta")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.name}
                className={`border-b border-zinc-100 last:border-0 ${r.delta !== 0 ? "bg-rose-50/60" : ""}`}
                data-testid={`recon-row-${holder.id}-${r.name}`}
              >
                <td className="py-2 pr-3 font-medium">{r.name}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.taken}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.logged_in}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.returns_in > 0 ? `+${r.returns_in}` : "—"}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.on_hand}</td>
                <td className={`py-2 pl-2 text-right tabular-nums font-bold ${r.delta !== 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {r.delta === 0 ? "✓" : r.delta > 0 ? `+${r.delta}` : r.delta}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BrutalCard>
  );
}
